// External dependencies
import { useEffect, useState } from "react";

// Material-UI components
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Grid,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { LocationMapping } from "pages/HomePage/data/HomePage.type";
import Spinner from "components/Spinner";
import { userRoleModel } from "./data/userRoleModel";
import { _updateUser } from "middlewares/ManageUsers/manageusers";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import {
  getFriendlyErrorMessage,
  reportUiError,
} from "services/ui-error-reporter";

interface AssignLocationProps {
  hideDialogHandler: () => void;
  getUserRoles: () => void;
  userRoleModel?: userRoleModel | null;
  handleCloseSnackBar: (state: AlertsProps) => void;
  locations: Array<LocationMapping>;
}

/**
 * AssignLocation component
 * 
 * This component renders a list of locations with checkboxes to assign them to a user.
 * It handles the selection and deselection of locations and saves the changes.
 *
 * @param {AssignLocationProps} props - The props for the component.
 * @param {Function} props.hideDialogHandler - Function to hide the dialog.
 * @param {Function} props.getUserRoles - Function to fetch user roles.
 * @param {userRoleModel | null} [props.userRoleModel] - Optional data for pre-filling the form when editing a user.
 * @param {Function} props.handleCloseSnackBar - Function to handle closing the snackbar.
 * @param {Array<LocationMapping>} props.locations - List of locations to display.
 * @returns {JSX.Element} A component for assigning locations to a user.
 */
const AssignLocation = (props: AssignLocationProps) => {
  const [locations, setLocations] = useState<
    Array<LocationMapping>
  >([]);
  const [loading, setLoading] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState<string>("");
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
  const [user, setUser] = useState<userRoleModel>(
    props.userRoleModel ?? {
      userRoleId: 0,
      userName: "",
      userEmail: "",
      company: "",
      roleId: "",
      locationId: 0,
      locationMapping: "",
    }
  );
  
  useEffect(() => {
    setLocations((prevLocations) => {
      const locationMapping = user.locationMapping ?? ""; // Default to empty string if null
      const updatedLocations = props.locations.map((loc) => ({
        ...loc,
        isAssigned: locationMapping.split(",").includes(String(loc.locationId)),
      }));
      return updatedLocations;
    });
  }, [props.locations]);

  useEffect(() => {
    setUser((prevUser) => ({
      ...prevUser,
      locationMapping: selectedLocations,
    }));
  }, [selectedLocations]);

  /**
   * Toggles the assignment status of a location.
   * 
   * @param {LocationMapping} loc - The location to toggle.
   * @param {number} index - The index of the location in the list.
   * @returns {LocationMapping} The updated location with toggled assignment status.
   */
  const toggleLocationAssignment = (loc: LocationMapping, index: number) => {
    return {
      ...loc,
      isAssigned: !loc.isAssigned,
    };
  };

  /**
   * Gets the IDs of the selected locations as a comma-separated string.
   * 
   * @param {LocationMapping[]} updatedLocations - The updated list of locations.
   * @returns {string} The IDs of the selected locations.
   */
  const getSelectedLocations = (updatedLocations: LocationMapping[]) => {
    return updatedLocations
      .filter((loc) => loc.isAssigned)
      .map((loc) => loc.locationId)
      .join(",");
  };

  /**
   * Handles the change event of the location checkboxes.
   * 
   * @param {LocationMapping} loc - The location whose checkbox was changed.
   * @param {number} index - The index of the location in the list.
   */
  const handleCheckboxChange = (loc: LocationMapping, index: number) => {
    setLocations((prevLocations) => {
      const updatedLocations = [...prevLocations];
      updatedLocations[index] = toggleLocationAssignment(loc, index);
      const newSelectedLocations = getSelectedLocations(updatedLocations);
      setSelectedLocations(newSelectedLocations);
      return updatedLocations;
    });
  };

   /**
   * Handles the save button click event, updating the user with the selected locations.
   */
  const handleSaveButtonClick = async () => {
    try {
      setLoading(true);
      setFormErrorMessage(null);
      const res = await _updateUser(user);
      if (res) {
        setLoading(false);
        const message = 'Data Saved Successfully !';
        props.handleCloseSnackBar({ show: true, severity: 'success', message: message });
        props.hideDialogHandler();
        props.getUserRoles();
      }
    } catch (err) {
      const message = getFriendlyErrorMessage(
        err,
        "We could not save assigned locations right now. Please try again."
      );
      setFormErrorMessage(message);
      reportUiError(err, {
        feature: "UserManagement",
        action: "assign locations",
        component: "AssignLocation",
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
        extra: {
          userRoleId: user.userRoleId,
          locationMapping: user.locationMapping,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Renders the list of locations with checkboxes.
   * 
   * @returns {JSX.Element} The rendered list of locations.
   */
  const renderLocations = () => (
    <List>
      {locations.map(
        (loc, index) =>
          loc.locationName !== "" && (
            <ListItem key={loc.locationId} >
              <Checkbox
                checked={loc.isAssigned ?? false}
                onClick={() => handleCheckboxChange(loc, index)}
                data-testid={`locCheckbox-${loc.locationId}`}
              />
              <ListItemText primary={loc.locationName} data-testid="locationName" />
            </ListItem>
          )
      )}
    </List>
  );

  // JSX structure for the AddUser component
  return (
    <Box>
      {formErrorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="assign-location-error-alert">
          {formErrorMessage}
        </Alert>
      )}
      <Grid item xs={12} md={12}>
        <br />
        {loading ? (
          <Spinner size={80} data-testid="spinner" />
        ) : (
          renderLocations()
        )}
      </Grid>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSaveButtonClick}
        data-testid="savebutton"
      >
        Save
      </Button>
    </Box>
  );
};

export default AssignLocation;
